import * as yup from 'yup'
import { DateTime } from 'luxon'
import { MedicalCardRequirement } from 'src/constants/collect-data/enums'
import { DOT_STATE_OF_ISSUE_OPTIONS } from 'src/constants/partnerDriver/constants'
import { DotNumberOption } from 'src/constants/partnerDriver/enums'
import type { CompleteDotNumberValidatorOptions } from './CompleteDotNumberForm.interface'

// The backend only checks `IsNumberString`; 1–8 digits is the FMCSA format the ticket asks for.
const DOT_NUMBER_PATTERN = /^\d{1,8}$/

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const STATE_VALUES: string[] = DOT_STATE_OF_ISSUE_OPTIONS.map((option) => {
  return option.value
})

const DATE_MESSAGE = 'Choose the date of the last submitted MCS-150'

const isNumberOption = (option: unknown): boolean => {
  return option === DotNumberOption.Dot || option === DotNumberOption.UsDot
}

const isValidIsoDate = (value: string | undefined): boolean => {
  return (
    Boolean(value) && ISO_DATE_PATTERN.test(value ?? '') && DateTime.fromISO(value ?? '').isValid
  )
}

/**
 * Mirrors the client app's DOT form (`DOTNumberForm.validator.ts`): the medical card file follows
 * the step's setting, and only the fields of the chosen option are validated. Skipping is allowed
 * only on an optional step — the backend refuses it otherwise.
 */
export const buildCompleteDotNumberValidator = ({
  medicalCardRequirement,
  isOptional,
  today,
}: CompleteDotNumberValidatorOptions) => {
  const allowedOptions: string[] = isOptional
    ? Object.values(DotNumberOption)
    : [DotNumberOption.Dot, DotNumberOption.UsDot]

  return yup.object({
    option: yup.string().test('option', 'Choose DOT or USDOT number', (value) => {
      return allowedOptions.includes(value ?? '')
    }),
    dotNumber: yup
      .string()
      .trim()
      .when('option', {
        is: DotNumberOption.Dot,
        then: (schema) => {
          return schema
            .required('Enter the DOT number')
            .matches(DOT_NUMBER_PATTERN, 'DOT number must be 1 to 8 digits')
        },
      }),
    // Not `oneOf`: Formik turns '' into undefined and yup 1.x `oneOf` skips undefined.
    stateOfIssue: yup.string().when('option', {
      is: DotNumberOption.Dot,
      then: (schema) => {
        return schema.test('state-of-issue', 'Choose the state of issue', (value) => {
          return STATE_VALUES.includes(value ?? '')
        })
      },
    }),
    usDotNumber: yup
      .string()
      .trim()
      .when('option', {
        is: DotNumberOption.UsDot,
        then: (schema) => {
          return schema
            .required('Enter the USDOT number')
            .matches(DOT_NUMBER_PATTERN, 'USDOT number must be 1 to 8 digits')
        },
      }),
    lastSubmittedAt: yup.string().when('option', {
      is: isNumberOption,
      then: (schema) => {
        return schema
          .test('valid-date', DATE_MESSAGE, (value) => {
            return isValidIsoDate(value)
          })
          .test('not-future', 'The date cannot be in the future', (value) => {
            // ISO dates compare correctly as strings.
            return !isValidIsoDate(value) || (value ?? '') <= today
          })
      },
    }),
    file: yup
      .mixed<File>()
      .nullable()
      .when('option', {
        is: (option: unknown) => {
          return (
            isNumberOption(option) && medicalCardRequirement === MedicalCardRequirement.Required
          )
        },
        then: (schema) => {
          return schema.test('required', 'Attach the DOT medical card', (value) => {
            return Boolean(value)
          })
        },
      }),
  })
}
